import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

export const SEARCH_DEBOUNCE_MS = 250
const MIN_QUERY_LENGTH = 2
// Dispatched (bubbling) by the ImoAgencyPicker island on every IMO/agency
// change — see app/frontend/react/mountImoAgencyPicker.tsx.
export const ORG_CHANGE_EVENT = "imo-agency-picker:change"
const IMO_FIELD = "coupon_code[audience_imo_id]"
const AGENCY_FIELD = "coupon_code[audience_agency_id]"

// Connects to data-controller="coupon-audience" — the "IMO/Agency Filters"
// card on the coupon form (#13071): ONE IMO, ONE agency under it, ONE agent.
//
// The IMO and Agency boxes are the shared React ImoAgencyPicker, mounted as a
// react_bridge island inside the `orgPicker` target (#13446). The island owns
// the IMO → Agency cascade, writes the two hidden fields the form submits and
// announces every change as `imo-agency-picker:change` (detail: imoId,
// agencyId, cleared). This controller owns only the AGENT: it scopes the agent
// typeahead (the admin user search with imo_organization_id /
// agency_organization_id filters) to the announced ids and reconciles a picked
// agent VISIBLY — one who is not in the new IMO/agency is cleared and the
// reason lands in the status line after the island's own message — so the form
// can never submit a stale id the server would reject anyway (the same
// hierarchy is validated server-side by CouponAudienceValidator).
//
// The agent picker is a native <input> + <datalist> like the Email Agent
// picker: it works on iOS inside the hosting modal, and the id only ever comes
// from a fetched result (agentsByLabel), never from typed text. Typing anything
// that is not an exact result label clears the hidden id.
export default class extends Controller {
  static targets = ["orgPicker", "agentInput", "agentOptions", "agentIdField", "status"]
  static values = { searchUrl: String }

  connect() {
    this.agentsByLabel = new Map()
    this.seedSelectedAgent()
    this._onOrgChange = (event) => this.orgChanged(event)
    this.element.addEventListener(ORG_CHANGE_EVENT, this._onOrgChange)
  }

  disconnect() {
    this.cancelPendingSearch()
    this.element.removeEventListener(ORG_CHANGE_EVENT, this._onOrgChange)
  }

  // A re-rendered form (422) carries the saved agent as label + hidden id (+ the
  // agent's org ids as data attributes), so the pick survives without a refetch.
  seedSelectedAgent() {
    if (!this.hasAgentIdFieldTarget || !this.hasAgentInputTarget) return
    const id = this.agentIdFieldTarget.value
    const label = this.agentInputTarget.value.trim()
    if (!id || !label) return
    this.agentsByLabel.set(label, {
      id: String(id),
      text: label,
      imoId: this.agentIdFieldTarget.dataset.imoId || "",
      agencyId: this.agentIdFieldTarget.dataset.agencyId || ""
    })
  }

  // ── IMO / Agency (announced by the island) ─────────────────────────────

  // The island announced a new IMO/agency pair and any child it cleared.
  // Re-check the picked agent against it and put every message in the one
  // status line — the picker's first, then ours.
  orgChanged(event) {
    const messages = [...(event.detail?.cleared ?? [])]
    if (this.reconcileAgent()) messages.push("Agent cleared: not a member of the selected IMO/agency.")
    this.announce(messages)
  }

  // ── Agent ──────────────────────────────────────────────────────────────

  agentSearch() {
    if (this.searchTimer) clearTimeout(this.searchTimer)

    const term = this.agentInputTarget.value.trim()
    const known = this.agentsByLabel.get(term)
    if (known) {
      // A <datalist> pick fires `input` with the full label: resolve the id and
      // stop — re-searching the label would find nothing and lose the pick.
      this.commitAgent(known)
      return
    }

    // Free text is never an id: clear so a stale pick can't ride along.
    this.commitAgent(null)
    if (term.length < MIN_QUERY_LENGTH) {
      this.renderAgentOptions([])
      return
    }
    this.searchTimer = setTimeout(() => this.performSearch(term), SEARCH_DEBOUNCE_MS)
  }

  async performSearch(term) {
    this.searchAbortController?.abort()
    const controller = new AbortController()
    this.searchAbortController = controller

    const query = { q: term }
    const imoId = this.selectedImoId()
    const agencyId = this.selectedAgencyId()
    if (imoId) query.imo_organization_id = imoId
    if (agencyId) query.agency_organization_id = agencyId

    try {
      const response = await get(this.searchUrlValue, { query, responseKind: "json", signal: controller.signal })
      if (controller.signal.aborted) return
      if (!response.ok) {
        this.renderAgentOptions([])
        return
      }
      const data = await response.json
      if (controller.signal.aborted) return
      this.renderAgentOptions(Array.isArray(data) ? data : [])
    } catch (e) {
      if (e?.name === "AbortError" || controller.signal.aborted) return
      this.renderAgentOptions([])
    }
  }

  // The endpoint returns [{ id, text, imo_organization_id, agency_organization_id }];
  // the org ids let a later parent change decide whether the pick still fits.
  renderAgentOptions(agents) {
    if (!this.hasAgentOptionsTarget) return

    this.agentsByLabel.clear()
    this.agentOptionsTarget.innerHTML = ""
    agents.forEach((agent) => {
      if (!agent || agent.id == null || !agent.text) return
      this.agentsByLabel.set(agent.text, {
        id: String(agent.id),
        text: agent.text,
        imoId: agent.imo_organization_id == null ? "" : String(agent.imo_organization_id),
        agencyId: agent.agency_organization_id == null ? "" : String(agent.agency_organization_id)
      })
      const option = document.createElement("option")
      option.value = agent.text
      this.agentOptionsTarget.appendChild(option)
    })
    this.setExpanded(this.agentsByLabel.size > 0)
  }

  setExpanded(expanded) {
    if (this.hasAgentInputTarget) this.agentInputTarget.setAttribute("aria-expanded", expanded ? "true" : "false")
  }

  // Stamps (or clears) the hidden id and remembers the pick's org ids.
  commitAgent(agent) {
    if (!this.hasAgentIdFieldTarget) return
    const field = this.agentIdFieldTarget
    field.value = agent ? agent.id : ""
    field.dataset.imoId = agent ? agent.imoId : ""
    field.dataset.agencyId = agent ? agent.agencyId : ""
    this.selectedAgent = agent
  }

  // Clears a picked agent who no longer fits the chosen IMO/agency. Returns
  // true when something was cleared so the caller can say so.
  reconcileAgent() {
    const agent = this.currentAgent()
    if (!agent) return false

    const imoId = this.selectedImoId()
    const agencyId = this.selectedAgencyId()
    const fits = (!imoId || agent.imoId === imoId) && (!agencyId || agent.agencyId === agencyId)
    if (fits) return false

    this.commitAgent(null)
    if (this.hasAgentInputTarget) this.agentInputTarget.value = ""
    this.renderAgentOptions([])
    return true
  }

  currentAgent() {
    if (!this.hasAgentIdFieldTarget || !this.agentIdFieldTarget.value) return null
    const field = this.agentIdFieldTarget
    return {
      id: field.value,
      imoId: field.dataset.imoId || "",
      agencyId: field.dataset.agencyId || ""
    }
  }

  // ── Shared ─────────────────────────────────────────────────────────────

  // The island's hidden fields are the source of truth for the chosen ids —
  // the same values the form submits.
  selectedImoId() { return this.hiddenValue(IMO_FIELD) }
  selectedAgencyId() { return this.hiddenValue(AGENCY_FIELD) }

  hiddenValue(name) {
    if (!this.hasOrgPickerTarget) return ""
    return this.orgPickerTarget.querySelector(`input[type="hidden"][name="${name}"]`)?.value ?? ""
  }

  // role="status" line under the card: visible reconciliation, never silent.
  announce(messages) {
    if (!this.hasStatusTarget) return
    const text = messages.join(" ")
    this.statusTarget.textContent = text
    this.statusTarget.classList.toggle("d-none", text.length === 0)
  }

  cancelPendingSearch() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
    this.searchAbortController?.abort()
    this.searchAbortController = null
  }
}
